import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Card, IconButton, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate, useSearchParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { ReadinessChip } from '../merchants/shared/StateChip'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { fmtDate, num } from '../ptl-merchant-setup/constants'
import { Banner, Breadcrumb } from '../ptl-merchant-setup/components/SetupUi'
import PtlPage from '../ptl-merchant-setup/shell/PtlPage'
import { errorText, fetchOdaHistory } from './ptlOdaApi'
import { HISTORY_ACTION, ODA_EYEBROW, ODA_ROUTES } from './constants'

const PAGE = 50
const PINCODE = /^[1-9]\d{5}$/

/**
 * PTL Operations → ODA Management → ODA history (PTL_ODA_REWORK_DEV_LOG.md): every ODA change of one courier code's
 * zone map rows (ODA type, km band, days by hand), by hand or by the ODA zone upload, and each ODA zone upload, newest
 * first, read from audit_record. ?code= and ?pincode= keep the view in the link.
 */
export default function PtlOdaHistory() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const code = params.get('code') || ''
  const pincode = params.get('pincode') || ''
  const [rows, setRows] = useState(null)
  const [more, setMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [pinInput, setPinInput] = useState(pincode)

  const load = useCallback((page) => {
    if (!code) return
    setLoading(true)
    setError('')
    const request = fetchOdaHistory({ code, pincode, page, size: PAGE })
    request
      .then((list) => { setRows((cur) => (page === 0 ? list : [...(cur || []), ...list])); setMore(list.length === PAGE) })
      .catch((e) => setError(errorText(e, 'Could not load the ODA history')))
      .finally(() => setLoading(false))
  }, [code, pincode])
  useEffect(() => { setRows(null); load(0) }, [load])

  const onPin = (v) => {
    const t = v.replace(/\D/g, '').slice(0, 6)
    setPinInput(t)
    if (t === '' || PINCODE.test(t)) {
      const next = new URLSearchParams(params)
      if (t) next.set('pincode', t)
      else next.delete('pincode')
      setParams(next, { replace: true })
    }
  }

  const list = rows || []
  const count = (a) => list.filter((r) => r.action === a).length
  const last = list[0]
  const kpis = [
    { key: 'changes', label: 'Changes', value: rows ? `${num(list.length)}${more ? '+' : ''}` : '', sub: pincode ? `For ${pincode}` : 'ODA type, band, days by hand, uploads' },
    { key: 'type', label: 'ODA type changes', value: rows ? num(count('mark') + count('unmark')) : '', sub: 'ODA 1 / 2 / 3 ↔ Regular' },
    { key: 'band', label: 'Band or days changes', value: rows ? num(count('band') + count('days')) : '', sub: 'EDD only' },
    { key: 'last', label: 'Last change', value: last ? fmtDate(last.at).split(',')[0] : 'None', sub: last ? [last.pincode, last.by].filter(Boolean).join(' · ') : 'No change yet' },
  ]
  const back = `${ODA_ROUTES.pincodes}?code=${encodeURIComponent(code)}`

  return (
    <PtlPage title={`ODA history · ${code}`} eyebrow={ODA_EYEBROW}
      description='Every ODA change of this courier code from ODA Management (Add or edit ODA pincode, Make Regular, ODA zone upload), with who and when: ODA type, km band and days set by hand, one line per value changed, plus one line per upload. Uploads from B2B Couriers are not listed.'
      breadcrumb={<Breadcrumb parent='ODA Pincodes' onParent={() => navigate(back)} current='ODA history' />}
      kpis={kpis} kpisLoading={!rows && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={() => load(0)} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}

      <Card elevation={0} sx={{ ...SX.card, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
          <TextField size='small' placeholder='Find a pincode' value={pinInput} onChange={(e) => onPin(e.target.value)}
            sx={{ width: { xs: '100%', sm: 220 }, bgcolor: C.surface }} inputProps={{ 'aria-label': 'Find a pincode', inputMode: 'numeric' }}
            InputProps={{
              startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: C.inkGhost }} /></InputAdornment>,
              endAdornment: pinInput ? (
                <InputAdornment position='end'>
                  <IconButton size='small' aria-label='Clear pincode' edge='end' onClick={() => onPin('')}><ClearIcon fontSize='small' /></IconButton>
                </InputAdornment>
              ) : null,
            }} />
        </Box>
        {rows && list.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <EmptyState dense title={pincode ? `No ODA change of ${pincode}` : `No ODA change for ${code} yet`}
              body='Changes made with Add or edit ODA pincode, Make Regular or the ODA zone upload show here.' />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 760 }} aria-label={`ODA changes of ${code}`}>
              <TableHead>
                <TableRow>
                  {['When', 'Pincode', 'Change', 'From → to', 'By'].map((h) => (
                    <TableCell key={h} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!rows && <TableRow><TableCell colSpan={5} sx={{ ...TYPE.rowHelp, textAlign: 'center', py: 3 }}>Loading history…</TableCell></TableRow>}
                {list.map((r, i) => (
                  <TableRow key={`${r.code}-${r.pincode}-${r.at}-${i}`} sx={{ '& td': { borderColor: C.lineSoft, py: 1.1 } }}>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, whiteSpace: 'nowrap' }}>{fmtDate(r.at)}</TableCell>
                    <TableCell sx={TYPE.rowLabel}>{r.pincode || 'Upload'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <ReadinessChip tone={(HISTORY_ACTION[r.action] || HISTORY_ACTION.upload).tone} label={(HISTORY_ACTION[r.action] || HISTORY_ACTION.upload).label} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody }}>
                      {r.action === 'upload' ? r.to : <Typography component='span' sx={{ fontSize: 12.5, color: C.inkBody }}>{r.from || '—'} → {r.to || '—'}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkMuted }}>{r.by || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {more && (
          <Box sx={{ textAlign: 'center', py: 1.25, borderTop: `1px solid ${C.line}` }}>
            <LoadingButton loading={loading} onClick={() => load(Math.ceil(list.length / PAGE))} sx={{ textTransform: 'none', fontWeight: 600 }}>Load older changes</LoadingButton>
          </Box>
        )}
      </Card>
    </PtlPage>
  )
}
